import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Slide,
  Typography
} from '@mui/material';
import DropdownMenu from 'src/components/DropdownMenu';
import {
  PaymentFormList,
  TransactionsType
} from 'src/utils/constants/transactions';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import Loading from 'src/components/Loading';
import initialState from './initialState';
import validationSchema from './schema';
import useStyles from '../styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RebillTransactionDialog = ({
  open,
  onClose,
  isLoading,
  onSubmit,
  title,
  chargeDescription,
  transactionType
}) => {
  const resetRef = useRef();
  const { classes } = useStyles();
  const { t } = useTranslation();

  if (!open) return null;

  if (transactionType === TransactionsType.CHARGE) {
    return (
      <ConfirmationDialog
        open={open}
        handleClose={onClose}
        onSubmit={onSubmit}
        isLoading={isLoading}
        title={title}
        description={chargeDescription}
      />
    );
  }

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.rootDialog }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle className={classes.header}>
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            onClose();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Loading />
        ) : (
          <Formik
            initialValues={initialState}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              handleSubmit,
              touched,
              resetForm,
              setFieldValue
            }) => {
              resetRef.current = () => resetForm();
              return (
                <form onSubmit={handleSubmit}>
                  <FormControl fullWidth>
                    <DropdownMenu
                      list={PaymentFormList}
                      value={values.paymentForm}
                      name="paymentForm"
                      setValue={value => setFieldValue('paymentForm', value)}
                      label={t('TRANSACTION.PAYMENT_FORM')}
                      error={Boolean(touched.paymentForm && errors.paymentForm)}
                      errorText={touched.paymentForm && t(errors.paymentForm)}
                    />
                  </FormControl>

                  <Grid
                    container
                    direction="column"
                    alignItems="stretch"
                    sx={{ marginBottom: 2 }}
                  >
                    <Grid item className={classes.buttonsDialog}>
                      <Button
                        type="submit"
                        color="primary"
                        variant="outlined"
                        className={clsx(classes.label, classes.buttonsDialog)}
                      >
                        {t('COMMON.CREATE')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

RebillTransactionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  transactionType: PropTypes.oneOf([
    TransactionsType.CHARGE,
    TransactionsType.PAYMENT
  ]),
  title: PropTypes.string,
  chargeDescription: PropTypes.string,
  onSubmit: PropTypes.func.isRequired
};

export default RebillTransactionDialog;
